/**
 * @file `enemy_ranged_01` — the configurable ranged-enemy template.
 *
 * The template is a *parameter table plus a validator*, never a class to
 * subclass. A new ranged enemy is created by copying the defaults and changing
 * numbers (see the `ranged` block of the rows in {@link ./Enemies.js}); the
 * behaviour itself lives once, in
 * {@link ../systems/EnemyRangedAISystem.js EnemyRangedAISystem}.
 *
 * Parameter names are kept exactly as specified so a designer reading the spec
 * can find them here without translation.
 */

import { WEAPONS } from './Weapons.js';
import { isDevBuild } from '../utils/Dev.js';

/**
 * States the template's machine can be in.
 * @readonly
 * @enum {string}
 */
export const RangedState = Object.freeze({
  IDLE_STATIC: 'Idle_Static',
  IDLE_ROAMING: 'Idle_Roaming',
  IDLE_PATROL: 'Idle_Patrol',
  AGGRO_RANGED: 'Aggro_Ranged'
});

/** Every state, in registration order. @type {readonly string[]} */
export const RANGED_STATES = Object.freeze(Object.values(RangedState));

/** The subset an enemy may fall back to when it loses its target. */
export const IDLE_STATES = Object.freeze([
  RangedState.IDLE_STATIC,
  RangedState.IDLE_ROAMING,
  RangedState.IDLE_PATROL
]);

/**
 * Combat patterns the template ships with.
 * @readonly
 * @enum {string}
 */
export const CombatPattern = Object.freeze({
  STATIC_SHOOTER: 'Static_Shooter',
  MOVING_SHOOTER: 'Moving_Shooter',
  STRAFE_SHOOTER: 'Strafe_Shooter',
  COVER_SHOOTER: 'Cover_Shooter',
  SHOOT_AND_HIDE: 'Shoot_And_Hide'
});

/** @type {readonly string[]} */
export const COMBAT_PATTERNS = Object.freeze(Object.values(CombatPattern));

/**
 * Template defaults. Every field is overridable per instance; anything omitted
 * or invalid falls back to the value below.
 *
 * @typedef {typeof RANGED_TEMPLATE_DEFAULTS} RangedTemplateConfig
 */
export const RANGED_TEMPLATE_DEFAULTS = Object.freeze({
  // --- General ---------------------------------------------------------------
  /** One of {@link RangedState}. */
  starter_state: RangedState.IDLE_PATROL,
  /** Key into {@link ./Weapons.js WEAPONS}. */
  enemy_weapon: 'spitter',

  // --- Movement --------------------------------------------------------------
  idle_walk_speed: 2.6,
  aggro_walk_speed: 4.4,
  navigation_arrival_tolerance: 0.7,
  navigation_repath_seconds: 0.8,
  navigation_stuck_detection_seconds: 1.1,
  navigation_stuck_distance_threshold: 0.35,
  idle_path_selection_max_attempts: 12,

  // --- Roaming ---------------------------------------------------------------
  idle_roaming_select_path_seconds: 3.4,
  idle_roaming_path_range: 9,

  // --- Patrol ----------------------------------------------------------------
  idle_patrol_select_path_seconds: 4.2,
  idle_patrol_path_range: 15,
  /** `<= 0` means the patrol is not leashed to the spawn point at all. */
  idle_patrol_max_distance_from_spawn: 0,

  // --- Detection -------------------------------------------------------------
  enemy_detection_cone_range: 26,
  /** Full aperture of the cone, in degrees; valid range is (0, 360]. */
  enemy_detection_cone_angle: 110,
  enemy_detection_check_seconds: 0.18,

  // --- Combat ----------------------------------------------------------------
  enemy_weapon_shot_seconds: 1.15,
  enemy_aggro_distance_min: 8,
  enemy_aggro_distance_max: 18,
  enemy_aggro_failed_shots_for_reposition: 3,
  enemy_aggro_target_memory_seconds: 5,
  /** `null` means "whatever `starter_state` was, as long as it is an idle state". */
  enemy_aggro_lost_target_return_state: null,
  aggro_default_combat_pattern: CombatPattern.STRAFE_SHOOTER,
  /**
   * Weighted pool the enemy draws from. Weights need not sum to 1 — they are
   * normalised on use. An empty pool pins the enemy to the default pattern.
   * @type {Record<string, number>}
   */
  aggro_combat_patterns: {
    [CombatPattern.STATIC_SHOOTER]: 0.3,
    [CombatPattern.STRAFE_SHOOTER]: 0.3,
    [CombatPattern.COVER_SHOOTER]: 0.2,
    [CombatPattern.SHOOT_AND_HIDE]: 0.2
  },
  aggro_pattern_minimum_duration_seconds: 3.2,
  aggro_pattern_maximum_duration_seconds: 7.5,
  aggro_moving_shot_speed_multiplier: 0.55,
  aggro_strafe_change_direction_seconds: 2.2,
  aggro_strafe_distance: 4.5,

  // --- Cover -----------------------------------------------------------------
  aggro_cover_search_range: 14,
  aggro_cover_minimum_height: 1.2,
  aggro_cover_reselection_seconds: 5,
  aggro_cover_fallback_pattern: CombatPattern.STRAFE_SHOOTER,
  aggro_shoot_and_hide_shots_per_cycle: 3,
  aggro_shoot_and_hide_hidden_seconds: 2.2,
  aggro_shoot_and_hide_exposed_seconds: 2.6,

  // --- Rotation --------------------------------------------------------------
  idle_rotation_speed: 3.5,
  aggro_rotation_speed: 9,
  /**
   * Optional ambient scan for `Idle_Static`: the enemy sweeps this many degrees
   * either side of its spawn facing. `0` keeps it perfectly still.
   */
  idle_static_scan_angle: 0
});

/** Parameters that must be a number greater than zero. */
const POSITIVE_TIMES = Object.freeze([
  'navigation_repath_seconds',
  'navigation_stuck_detection_seconds',
  'idle_roaming_select_path_seconds',
  'idle_patrol_select_path_seconds',
  'enemy_detection_check_seconds',
  'enemy_weapon_shot_seconds',
  'enemy_aggro_target_memory_seconds',
  'aggro_pattern_minimum_duration_seconds',
  'aggro_pattern_maximum_duration_seconds',
  'aggro_strafe_change_direction_seconds',
  'aggro_cover_reselection_seconds',
  'aggro_shoot_and_hide_hidden_seconds',
  'aggro_shoot_and_hide_exposed_seconds'
]);

/** Parameters that may not be negative. */
const NON_NEGATIVE = Object.freeze([
  'idle_walk_speed',
  'aggro_walk_speed',
  'navigation_arrival_tolerance',
  'navigation_stuck_distance_threshold',
  'idle_roaming_path_range',
  'idle_patrol_path_range',
  'enemy_detection_cone_range',
  'enemy_aggro_distance_min',
  'enemy_aggro_distance_max',
  'aggro_strafe_distance',
  'aggro_cover_search_range',
  'aggro_cover_minimum_height',
  'idle_rotation_speed',
  'aggro_rotation_speed',
  'idle_static_scan_angle'
]);

/** Parameters that must be an integer of at least one. */
const POSITIVE_COUNTS = Object.freeze([
  'idle_path_selection_max_attempts',
  'enemy_aggro_failed_shots_for_reposition',
  'aggro_shoot_and_hide_shots_per_cycle'
]);

/**
 * Resolves one instance's parameters against the template defaults, reporting
 * every rejected value.
 *
 * @param {Partial<RangedTemplateConfig>} [overrides] Instance parameters.
 * @param {string} [instanceName] Name used in warnings.
 * @param {(message: string) => void} [warn] Warning sink; defaults to a dev-only console warning.
 * @returns {RangedTemplateConfig} A frozen, fully populated configuration.
 */
export function resolveRangedConfig(
  overrides = {},
  instanceName = 'enemy_ranged_01',
  warn = defaultWarn
) {
  /** @type {any} */
  const config = { ...RANGED_TEMPLATE_DEFAULTS, ...overrides };

  /**
   * @param {string} key Parameter name.
   * @param {any} received Rejected value.
   * @param {any} fallback Value used instead.
   * @returns {void}
   */
  const reject = (key, received, fallback) => {
    config[key] = fallback;
    warn(
      `[${instanceName}] invalid "${key}": received ${format(received)}, ` +
        `using ${format(fallback)}.`
    );
  };

  for (const key of POSITIVE_TIMES) {
    if (!(typeof config[key] === 'number' && Number.isFinite(config[key]) && config[key] > 0)) {
      reject(key, config[key], RANGED_TEMPLATE_DEFAULTS[key]);
    }
  }
  for (const key of NON_NEGATIVE) {
    if (!(typeof config[key] === 'number' && Number.isFinite(config[key]) && config[key] >= 0)) {
      reject(key, config[key], RANGED_TEMPLATE_DEFAULTS[key]);
    }
  }
  for (const key of POSITIVE_COUNTS) {
    if (!(Number.isInteger(config[key]) && config[key] >= 1)) {
      reject(key, config[key], RANGED_TEMPLATE_DEFAULTS[key]);
    }
  }

  // A leash of zero or less is the documented "no limit", so only a non-number
  // is wrong here.
  if (typeof config.idle_patrol_max_distance_from_spawn !== 'number' ||
      !Number.isFinite(config.idle_patrol_max_distance_from_spawn)) {
    reject(
      'idle_patrol_max_distance_from_spawn',
      config.idle_patrol_max_distance_from_spawn,
      RANGED_TEMPLATE_DEFAULTS.idle_patrol_max_distance_from_spawn
    );
  }

  if (typeof config.aggro_moving_shot_speed_multiplier !== 'number' ||
      !(config.aggro_moving_shot_speed_multiplier > 0)) {
    reject(
      'aggro_moving_shot_speed_multiplier',
      config.aggro_moving_shot_speed_multiplier,
      RANGED_TEMPLATE_DEFAULTS.aggro_moving_shot_speed_multiplier
    );
  }

  if (!(config.enemy_detection_cone_angle > 0 && config.enemy_detection_cone_angle <= 360)) {
    reject(
      'enemy_detection_cone_angle',
      config.enemy_detection_cone_angle,
      RANGED_TEMPLATE_DEFAULTS.enemy_detection_cone_angle
    );
  }

  if (config.enemy_aggro_distance_min > config.enemy_aggro_distance_max) {
    reject('enemy_aggro_distance_min', config.enemy_aggro_distance_min, config.enemy_aggro_distance_max);
  }

  if (config.aggro_pattern_minimum_duration_seconds > config.aggro_pattern_maximum_duration_seconds) {
    reject(
      'aggro_pattern_minimum_duration_seconds',
      config.aggro_pattern_minimum_duration_seconds,
      config.aggro_pattern_maximum_duration_seconds
    );
  }

  if (!RANGED_STATES.includes(config.starter_state)) {
    reject('starter_state', config.starter_state, RangedState.IDLE_STATIC);
  }

  if (!WEAPONS[config.enemy_weapon]) {
    reject('enemy_weapon', config.enemy_weapon, RANGED_TEMPLATE_DEFAULTS.enemy_weapon);
  }

  if (!COMBAT_PATTERNS.includes(config.aggro_default_combat_pattern)) {
    reject(
      'aggro_default_combat_pattern',
      config.aggro_default_combat_pattern,
      RANGED_TEMPLATE_DEFAULTS.aggro_default_combat_pattern
    );
  }

  if (!COMBAT_PATTERNS.includes(config.aggro_cover_fallback_pattern)) {
    reject(
      'aggro_cover_fallback_pattern',
      config.aggro_cover_fallback_pattern,
      RANGED_TEMPLATE_DEFAULTS.aggro_cover_fallback_pattern
    );
  }
  // A cover pattern falling back to a cover pattern would loop forever.
  if (
    config.aggro_cover_fallback_pattern === CombatPattern.COVER_SHOOTER ||
    config.aggro_cover_fallback_pattern === CombatPattern.SHOOT_AND_HIDE
  ) {
    reject(
      'aggro_cover_fallback_pattern',
      config.aggro_cover_fallback_pattern,
      RANGED_TEMPLATE_DEFAULTS.aggro_cover_fallback_pattern
    );
  }

  config.aggro_combat_patterns = sanitizePatterns(
    config.aggro_combat_patterns,
    config.aggro_default_combat_pattern,
    instanceName,
    warn
  );

  config.enemy_aggro_lost_target_return_state = resolveReturnState(
    config.enemy_aggro_lost_target_return_state,
    config.starter_state,
    instanceName,
    warn
  );

  Object.freeze(config.aggro_combat_patterns);
  return Object.freeze(config);
}

/**
 * @param {any} raw Configured pool.
 * @param {string} defaultPattern Pattern used when nothing valid survives.
 * @param {string} instanceName Instance label for warnings.
 * @param {(message: string) => void} warn Warning sink.
 * @returns {Record<string, number>}
 */
function sanitizePatterns(raw, defaultPattern, instanceName, warn) {
  /** @type {Record<string, number>} */
  const pool = {};

  if (raw && typeof raw === 'object') {
    for (const [pattern, weight] of Object.entries(raw)) {
      if (!COMBAT_PATTERNS.includes(pattern)) {
        warn(`[${instanceName}] invalid "aggro_combat_patterns" entry: unknown pattern "${pattern}", ignored.`);
        continue;
      }
      if (!(typeof weight === 'number' && Number.isFinite(weight) && weight > 0)) {
        warn(
          `[${instanceName}] invalid weight for pattern "${pattern}": received ` +
            `${format(weight)}, entry ignored.`
        );
        continue;
      }
      pool[pattern] = weight;
    }
  } else if (raw !== undefined && raw !== null) {
    warn(`[${instanceName}] invalid "aggro_combat_patterns": received ${format(raw)}, using the default pattern only.`);
  }

  if (Object.keys(pool).length === 0) pool[defaultPattern] = 1;
  return pool;
}

/**
 * @param {any} configured Configured return state.
 * @param {string} starterState Already-validated starter state.
 * @param {string} instanceName Instance label for warnings.
 * @param {(message: string) => void} warn Warning sink.
 * @returns {string} An idle state.
 */
function resolveReturnState(configured, starterState, instanceName, warn) {
  const fallback = IDLE_STATES.includes(starterState) ? starterState : RangedState.IDLE_STATIC;
  if (configured === null || configured === undefined) return fallback;
  if (IDLE_STATES.includes(configured)) return configured;

  warn(
    `[${instanceName}] invalid "enemy_aggro_lost_target_return_state": received ` +
      `${format(configured)}, using ${format(fallback)}.`
  );
  return fallback;
}

/**
 * Draws a combat pattern out of the configured weighted pool.
 *
 * @param {RangedTemplateConfig} config Resolved configuration.
 * @param {() => number} [random] Random source, injected for tests.
 * @returns {string} One of {@link CombatPattern}.
 */
export function pickCombatPattern(config, random = Math.random) {
  const pool = config.aggro_combat_patterns;
  const patterns = Object.keys(pool);
  if (patterns.length === 0) return config.aggro_default_combat_pattern;
  if (patterns.length === 1) return patterns[0];

  let total = 0;
  for (const pattern of patterns) total += pool[pattern];

  let roll = random() * total;
  for (const pattern of patterns) {
    roll -= pool[pattern];
    if (roll <= 0) return pattern;
  }
  return patterns[patterns.length - 1];
}

/**
 * @param {any} value Value to render inside a warning.
 * @returns {string}
 */
function format(value) {
  if (typeof value === 'string') return `"${value}"`;
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * @param {string} message Warning text.
 * @returns {void}
 */
function defaultWarn(message) {
  if (isDevBuild()) console.warn(message);
}
